import {
  Chip,
  Stack,
  Tooltip,
  Typography,
  Avatar,
  AvatarGroup,
  alpha,
  SvgIcon,
  Card,
  CardContent,
  Button,
  IconButton,
  Box,
  debounce,
  Skeleton,
  TextField,
  Autocomplete,
  InputAdornment,
  TooltipProps,
  tooltipClasses,
  Link,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnGroupingModel,
  GridColumnHeaders,
  GridPaginationModel,
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridLogicOperator,
  GridToolbar,
  GridSortModel,
  GridColumnsPanel,
} from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
import Iconify from 'src/components/iconify';
import { FilterPanelCustom } from 'src/components/data-grid/filter-panel-custom';
import { styled } from '@mui/material/styles';
import { useLocales } from 'src/locales';
import { useNonBrandedContext } from '../context/use-non-branded';
import { useExportMutation } from 'src/context/api/exports/api';
import { enqueueSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useBuildColumns, useSearchTermsGridData } from './hook';

const COMPARISON_SUFFIXES = ['_compare', '_diff'] as const;

const getBaseField = (field: string) =>
  COMPARISON_SUFFIXES.reduce((baseField, suffix) => baseField.replace(suffix, ''), field);

const isComparisonField = (field: string) =>
  COMPARISON_SUFFIXES.some((suffix) => field.endsWith(suffix));

const normalizeColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
  const normalized = { ...model };

  Object.keys(normalized).forEach((field) => {
    if (!isComparisonField(field)) {
      return;
    }

    const baseField = getBaseField(field);
    if (normalized[baseField] === false) {
      normalized[field] = false;
    }
  });

  return normalized;
};

const DEFAULT_VISIBLE_COLUMNS = new Set<string>([
  'search_term',
  'monthly_search_volumes',
  'visibility_rate',
  'top5_compet_total',
  'avg_seo_position',
  'impr_per_search',
  'ai_overview_coverage',
  'clicks_split',
  'blended_ctr',
  'conversions',
  'cvr',
  'ad_cost',
  'opportunity',
]);

export default function SearchTermsGrid() {
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const [exportMutation] = useExportMutation();
  const { selectedRegion } = useNonBrandedContext();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'search_term', sort: 'desc' },
  ]);
  const { searchTermsGrid, isLoading, isError, isFetching, job_id } = useSearchTermsGridData(
    Object.assign(
      {},
      { ...filterModel, items: filterModel.items.filter((item) => ![null, undefined, ''].includes(item.value)) },
      paginationModel,
      { countries: selectedRegion ? [selectedRegion] : [] }
    ),
    sortModel
  );
  const opportunityMap: Record<string, string> = {
    no_opportunity: t('dashboard.nonBrand.opportunities.noOpportunity'),
    overlap: t('dashboard.nonBrand.opportunities.overlap'),
    seo_boost: t('dashboard.nonBrand.opportunities.organicBoost'),
    sea_boost: t('dashboard.nonBrand.opportunities.cpcBoost'),
  };

  const apiRef = useRef<any>(null);
  const columns = useBuildColumns('search-terms');

  const check = (value: any) => {
    if (value === undefined) {
      return false;
    }
    if (value === null) {
      return false;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string') {
      return value !== '';
    }
    if (Array.isArray(value)) {
      return value.some(check);
    }
    if (typeof value === 'object') {
      return check(Object.values(value));
    }
    return false;
  }

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    normalizeColumnVisibilityModel(
      columns.reduce((acc, column) => {
        const baseField = getBaseField(column.field);
        const datacol = searchTermsGrid?.data?.map((row: any) => row[baseField]).some(check);
        acc[column.field] = DEFAULT_VISIBLE_COLUMNS.has(baseField) && datacol;
        return acc;
      }, {} as GridColumnVisibilityModel)
    )
  );

  useEffect(() => {
    setColumnVisibilityModel((currentModel) => {
      const nextModel: GridColumnVisibilityModel = { ...currentModel };
      let hasChanged = false;
      const availableFields = new Set(columns.map((column) => column.field));

      columns.forEach((column) => {
        if (nextModel[column.field] !== undefined) {
          return;
        }

        const baseField = getBaseField(column.field);
        const baseIsVisible = nextModel[baseField] !== false;
        nextModel[column.field] = isComparisonField(column.field)
          ? baseIsVisible
          : DEFAULT_VISIBLE_COLUMNS.has(baseField);
        hasChanged = true;
      });

      Object.keys(nextModel).forEach((field) => {
        if (availableFields.has(field)) {
          return;
        }
        delete nextModel[field];
        hasChanged = true;
      });

      const normalizedModel = normalizeColumnVisibilityModel(nextModel);
      if (
        !hasChanged &&
        JSON.stringify(normalizedModel) === JSON.stringify(currentModel)
      ) {
        return currentModel;
      }
      return normalizedModel;
    });
  }, [columns]);

  const handleColumnVisibilityModelChange = useCallback((nextModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(normalizeColumnVisibilityModel(nextModel));
  }, []);
  const {
    selectedOpportunity: selectedOpportunityContext,
    setSelectedOpportunity: setSelectedOpportunityContext,
  } = useNonBrandedContext();

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const HeaderGroup = (props: any) => {
    return (
      <Stack direction="row" alignItems="center" spacing={0}>
        <Typography
          variant="body1"
          noWrap
          sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
        >
          {props.groupId}
        </Typography>
      </Stack>
    );
  };

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'Visibility',
      children: [
        { field: 'visibility_rate' },
        { field: 'top5_compet_total' },
        { field: 'top5_search_rate' },
        { field: 'top5_shopping_rate' },
        { field: 'top5_organic_rate' },
        { field: 'avg_seo_position' },
        { field: 'paid_search_share' },
        { field: 'paid_shopping_share' },
        { field: 'impressions' },
        { field: 'ai_overview_coverage' },
        { field: 'visibility_rate_compare' },
        { field: 'top5_compet_total_compare' },
        { field: 'top5_search_rate_compare' },
        { field: 'top5_shopping_rate_compare' },
        { field: 'top5_organic_rate_compare' },
        { field: 'avg_seo_position_compare' },
        { field: 'paid_search_share_compare' },
        { field: 'paid_shopping_share_compare' },
        { field: 'impressions_compare' },
        { field: 'visibility_rate_diff' },
        { field: 'top5_compet_total_diff' },
        { field: 'top5_search_rate_diff' },
        { field: 'top5_shopping_rate_diff ' },
        { field: 'top5_organic_rate_diff' },
        { field: 'avg_seo_position_diff' },
        { field: 'paid_search_share_diff' },
        { field: 'paid_shopping_share_diff' },
        { field: 'impressions_diff' },
      ],
      headerClassName: 'visibility-group-header',
    },
    {
      groupId: 'Traffic',
      children: [
        { field: 'clicks_split' },
        { field: 'blended_ctr' },
        { field: 'organic_ctr' },
        { field: 'paid_search_ctr' },
        { field: 'paid_shopping_ctr' },
        { field: 'clicks_split_compare' },
        { field: 'blended_ctr_compare' },
        { field: 'organic_ctr_compare' },
        { field: 'paid_search_ctr_compare' },
        { field: 'paid_shopping_ctr_compare' },
        { field: 'clicks_split_diff' },
        { field: 'blended_ctr_diff' },
        { field: 'organic_ctr_diff' },
        { field: 'paid_search_ctr_diff' },
        { field: 'paid_shopping_ctr_diff' },
      ],
      headerClassName: 'traffic-group-header',
    },
    {
      groupId: 'Conversion',
      children: [
        { field: 'conversions' },
        { field: 'cvr' },
        { field: 'organic_cvr' },
        { field: 'paid_search_cvr' },
        { field: 'paid_shopping_cvr' },
        { field: 'conversions_compare' },
        { field: 'cvr_compare' },
        { field: 'organic_cvr_compare' },
        { field: 'paid_search_cvr_compare' },
        { field: 'paid_shopping_cvr_compare' },
        { field: 'conversions_diff' },
        { field: 'cvr_diff' },
        { field: 'organic_cvr_diff' },
        { field: 'paid_search_cvr_diff' },
        { field: 'paid_shopping_cvr_diff' },
      ],
      headerClassName: 'conversion-group-header',
    },
  ];


  const Toolbar = (props: any) => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0}
        px={2.5}
        py={1.25}
        sx={{
          borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
          minHeight: 46,
        }}
      >
        <Stack direction="row" alignItems="center" px={1} pl={0} flexGrow={1} spacing={0.5}>
          <IconButton
            onClick={() => apiRef.current?.showPreferences('columns')}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
          </IconButton>
          <IconButton
            onClick={() => apiRef.current?.showFilterPanel()}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="ic:round-filter-list" width={18} height={18} />
          </IconButton>
          <LoadingButton
            loading={isLoading || isFetching}
            startIcon={<Iconify icon="lets-icons:export" width={16} height={16} />}
            onClick={() => {
              if (job_id) {
                exportMutation({
                  name: `[${workspace?.name}] (Non Branded) Search terms export`,
                  queryId: job_id,
                  columns: columns
                    .map((column) => column.field)
                    .filter((column) => {
                      return (
                        columnVisibilityModel[column as keyof typeof columnVisibilityModel] !==
                        false
                      );
                    }),
                  column_tr: columns.reduce(
                    (acc: Record<string, string>, column: GridColDef) => {
                      acc[column.field] = column.headerName as string;
                      return acc;
                    },
                    {} as Record<string, string>
                  ),
                  dimensions: ['search_term'],
                })
                  .unwrap()
                  .then((res) => {
                    enqueueSnackbar(
                      "Export is running in background, you will be notified when it's done",
                      { variant: 'success' }
                    );
                  })
                  .catch((err) => {
                    enqueueSnackbar('Error exporting data', { variant: 'error' });
                  });
              } else {
                enqueueSnackbar('Unable to export this query', { variant: 'error' });
              }
            }}
            sx={{
              minWidth: 24,
              minHeight: 24,
              maxWidth: 24,
              maxHeight: 24,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
          </LoadingButton>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Autocomplete
            size="small"
            options={[
              { label: t('dashboard.nonBrand.opportunities.overlap'), value: 'overlap' },
              { label: t('dashboard.nonBrand.opportunities.organicBoost'), value: 'seo_boost' },
              { label: t('dashboard.nonBrand.opportunities.cpcBoost'), value: 'sea_boost' },
              {
                label: t('dashboard.nonBrand.opportunities.noOpportunity'),
                value: 'no_opportunity',
              },
            ]}
            onChange={(event, newValue) =>
              setSelectedOpportunityContext(newValue?.value || (null as any))
            }
            fullWidth
            multiple={false}
            value={
              selectedOpportunityContext
                ? { label: selectedOpportunityContext, value: selectedOpportunityContext }
                : null
            }
            renderOption={(props, option) => (
              <li {...props}>
                <Chip
                  className={`opportunity-chip opportunity-chip-${option.value}`}
                  label={option.label}
                  size="small"
                  variant="soft"
                />
              </li>
            )}
            renderInput={(params) => (
              <>
                <TextField
                  {...params}
                  value={''}
                  inputProps={{ ...params.inputProps, value: '' }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: undefined,
                    startAdornment: selectedOpportunityContext ? (
                      <InputAdornment position="start">
                        <Chip
                          className={`opportunity-chip opportunity-chip-${selectedOpportunityContext}`}
                          label={opportunityMap[selectedOpportunityContext]}
                          size="small"
                          variant="soft"
                          onDelete={() => setSelectedOpportunityContext(null as any)}
                        />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ width: 185 }}
                  size="small"
                  placeholder={
                    selectedOpportunityContext
                      ? undefined
                      : t('dashboard.nonBrand.columns.opportunity')
                  }
                />
              </>
            )}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(145, 158, 171, 0.12)',
        overflow: 'hidden',
      }}
    >
      <CardContent
        sx={{
          px: 0,
          py: 0,
          overflow: 'visible',
          minHeight: isLoading || isFetching ? 600 : 'auto',
        }}
      >
        <DataGridPro
          classes={{
            root: 'search-term-grid',
            columnHeaders: 'search-term-grid-column-headers',
            columnHeader: 'search-term-grid-column-header',
          }}
          apiRef={apiRef}
          columnGroupHeaderHeight={26}
          pinnedColumns={{ left: ['search_term'], right: ['opportunity'] }}
          loading={isLoading || isFetching}
          rows={isLoading || isFetching ? [] : searchTermsGrid?.data || []}
          columns={columns}
          getRowHeight={() => 60}
          disableColumnFilter={true}
          disableColumnMenu={true}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          filterModel={filterModel}
          filterMode="server"
          sortingMode="server"
          columnGroupingModel={columnGroupingModel}
          disableColumnSelector={true}
          disableMultipleColumnsSorting={true}
          onSortModelChange={setSortModel}
          sortModel={sortModel}
          onFilterModelChange={setFilterModel}
          disableDensitySelector
          checkboxSelection={false}
          pagination
          autoHeight
          disableRowSelectionOnClick={true}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={searchTermsGrid?.total_rows || 0}
          pageSizeOptions={[10, 20, 50, 100]}
          sx={{
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(145, 158, 171, 0.12)',
              minHeight: 53,
            },
          }}
          slots={{
            toolbar: Toolbar,
            filterPanel: FilterPanelCustom,
            loadingOverlay: (props) => <Skeleton variant="rectangular" height={60 * (paginationModel.pageSize + 2)} />,
          }}
          slotProps={{
            toolbar: {
              printOptions: {
                disableToolbarButton: true,
              },
              csvOptions: {
                disableToolbarButton: true,
              },
            },
            filterPanel: {
              logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
              getColumnForNewFilter: (args) => (args ? args.columns[0].field : null),
            },
            loadingOverlay: {
              style: {
                backgroundColor: 'unset',

              },
            },
            columnsManagement: {
              getTogglableColumns: (columns) => columns.filter((column: any) => column.visibility !== null).map((column: any) => column.field),
            }
          }}
        />
      </CardContent>
    </Card>
  );
}